import { useMemo } from 'react'
import moment from 'moment'

import { EmptyObject } from 'lib/object/objectUtils'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import Group from 'components/utils/Group'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferTilePricing from 'components/OfferList/OfferListTiles/Support/OfferTilePricing'
import SearchTileFlightOnlyPricing from 'components/OfferList/OfferListTiles/Support/SearchTileFlightOnlyPricing'
import SearchTileWalledMessage from '../shared/SearchTileWalledMessage'
import WalledContent from 'components/Common/WalledContent'
import { calculateOfferTileDiscountPercentage } from 'lib/search/searchUtils'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { MALDIVES_BUNDLE_DEFAULT_GUESTS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import CharterBundlePricing from './CharterBundlePricing'

interface Props {
  bestPackage?: App.HotelPackage
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary
  bestRate?: App.OfferAvailableRate
  duration?: number
  hidePrice?: boolean
  showPerNightPricing?: boolean
  hideIncludesTaxesAndFees?: boolean
}

function HotelSearchTilePriceStack({
  bestPackage,
  filters,
  offer,
  bestRate,
  duration,
  hidePrice = false,
  showPerNightPricing = false,
  hideIncludesTaxesAndFees = false,
}: Props) {
  const hasDates = !!(filters?.checkIn && filters?.checkOut && filters?.rooms?.length)
  const checkInDate = useMemo(() => {
    return filters?.checkIn ? moment(filters.checkIn) : undefined
  }, [filters?.checkIn])

  const directSearchPrices = useDirectSearchPrices({ filters: filters ?? EmptyObject, offerId: offer.id })
  const discountPercentage = useMemo(() => {
    if (offer.isDiscountPillHidden) {
      return undefined
    }

    if (directSearchPrices?.lowestPrice && directSearchPrices?.lowestPriceValue) {
      return calculateOfferTileDiscountPercentage({
        price: directSearchPrices.lowestPrice,
        value: directSearchPrices.lowestPriceValue,
      })
    }

    if (bestPackage) {
      return calculateOfferTileDiscountPercentage({
        price: bestPackage.price,
        value: bestPackage.value,
      })
    }

    return undefined
  }, [bestPackage, directSearchPrices, offer])

  const offerMetaData = useOfferMetaData(offer.id, filters)

  const isCharterBundleOffer = offer.offerFlightsEnabled &&
    offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)

  const charterSearchPrices = offerMetaData?.pricing
  if (isCharterBundleOffer && charterSearchPrices?.lowestPrice) {
    const totalGuests = filters?.rooms ? sumUpOccupancies(filters.rooms) : MALDIVES_BUNDLE_DEFAULT_GUESTS
    const bundleDiscountPercent = charterSearchPrices.lowestPriceValue
      ? getValueOffPercent(charterSearchPrices.lowestPriceValue, charterSearchPrices.lowestPrice)
      : undefined
    return <CharterBundlePricing
      offer={offer}
      pricing={{
        price: charterSearchPrices.lowestPrice,
        memberPrice: charterSearchPrices.lowestMemberPrice,
        value: charterSearchPrices.lowestPriceValue,
        discountPercent: bundleDiscountPercent,
        totalGuests,
        roomCount: filters?.rooms?.length,
      }}
      isPricePerPerson
      nights={charterSearchPrices.duration ?? duration}
    />
  }

  return <div>
    {!bestPackage && <LoadingIndicator inline ballSize={12} />}
    {bestPackage && <WalledContent
      enforced={offer.walledGarden}
      wall={<SearchTileWalledMessage discountPercentage={discountPercentage} />}
    >
      <Group direction="vertical" gap={8} fullWidth>
        {!offer.bundledWithFlightsOnly && <OfferTilePricing
          offer={offer}
          hasDates={hasDates}
          checkInDate={checkInDate}
          pkg={bestPackage}
          availableRate={bestRate}
          align="start"
          filters={filters}
          duration={duration}
          hidePrice={hidePrice}
          overrideRoomsNumber={offerMetaData?.rooms.length}
          isPricePerNight={showPerNightPricing}
          hideIncludesTaxesAndFees={hideIncludesTaxesAndFees}
        />}
        {(offer.bundledWithFlightsOnly && !hidePrice) && (
          <SearchTileFlightOnlyPricing
            offer={offer}
            rooms={filters?.rooms}
            hasDates={hasDates}
            pkg={bestPackage}
            pricing={bestRate}
            filters={filters}
          />
        )}
      </Group>
    </WalledContent>}
  </div>
}

export default HotelSearchTilePriceStack
